class SelectionManager {
  constructor(options = {}) {
    this.selectionRange = null
    this.editorRef = null

    // Bind methods
    this.initialize = this.initialize.bind(this)
    this.updateSelection = this.updateSelection.bind(this)
    this.getSelection = this.getSelection.bind(this)
    this.setSelection = this.setSelection.bind(this)
    this.saveSelection = this.saveSelection.bind(this)
    this.restoreSelection = this.restoreSelection.bind(this)
    this.collapseToEnd = this.collapseToEnd.bind(this)
    this.selectElement = this.selectElement.bind(this)
    this.insertAtCursor = this.insertAtCursor.bind(this)
    this.isSelectionInEditor = this.isSelectionInEditor.bind(this)
  }

  initialize({ editorRef }) {
    this.editorRef = editorRef
  }

  updateSelection() {
    const selection = window.getSelection()
    this.selectionRange = selection.rangeCount > 0 ? selection.getRangeAt(0) : null
    return this.selectionRange
  }

  getSelection() {
    return this.selectionRange
  }

  setSelection(range) {
    if (!range) return

    this.selectionRange = range
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
  }

  saveSelection() {
    const selection = window.getSelection()
    if (selection.rangeCount > 0) {
      try {
        const range = selection.getRangeAt(0)
        return {
          anchorNode: selection.anchorNode,
          anchorOffset: selection.anchorOffset,
          focusNode: selection.focusNode,
          focusOffset: selection.focusOffset,
          range: range.cloneRange(),
          selectedElement:
            selection.anchorNode?.nodeType === Node.TEXT_NODE
              ? selection.anchorNode.parentElement
              : selection.anchorNode,
        }
      } catch (e) {
        console.warn("Failed to save selection:", e)
        return null
      }
    }
    return null
  }

  restoreSelection(savedSelection, fallbackElement = null) {
    if (!savedSelection) {
      if (fallbackElement) {
        this.selectElement(fallbackElement)
      }
      return false
    }

    try {
      const selection = window.getSelection()

      // Try to restore the exact selection first
      if (
        savedSelection.anchorNode &&
        savedSelection.anchorNode.parentNode &&
        document.contains(savedSelection.anchorNode)
      ) {
        const range = document.createRange()
        range.setStart(savedSelection.anchorNode, savedSelection.anchorOffset)
        range.setEnd(
          savedSelection.focusNode || savedSelection.anchorNode,
          savedSelection.focusOffset || savedSelection.anchorOffset
        )
        selection.removeAllRanges()
        selection.addRange(range)
        this.selectionRange = range
        return true
      } else if (savedSelection.selectedElement && document.contains(savedSelection.selectedElement)) {
        // Fallback to selecting the element
        this.selectElement(savedSelection.selectedElement)
        return true
      } else if (fallbackElement) {
        // Final fallback
        this.selectElement(fallbackElement)
        return true
      }
    } catch (e) {
      console.warn("Failed to restore selection:", e)
      if (fallbackElement) {
        this.selectElement(fallbackElement)
      }
      return false
    }

    return false
  }

  selectElement(element, collapseToEnd = true) {
    if (!element) return false

    try {
      const selection = window.getSelection()
      const range = document.createRange()

      range.selectNodeContents(element)
      if (collapseToEnd) {
        range.collapse(false) // Collapse to end
      }

      selection.removeAllRanges()
      selection.addRange(range)
      this.selectionRange = range
      return true
    } catch (e) {
      console.warn("Failed to select element:", e)
      return false
    }
  }

  collapseToEnd() {
    const selection = window.getSelection()
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      range.collapse(false)
      selection.removeAllRanges()
      selection.addRange(range)
      this.selectionRange = range
    }
  }

  insertAtCursor(node) {
    if (!this.selectionRange) {
      this.updateSelection()
    }

    if (this.selectionRange) {
      try {
        this.selectionRange.deleteContents()
        this.selectionRange.insertNode(node)

        // Move cursor after inserted node
        const newRange = document.createRange()
        newRange.setStartAfter(node)
        newRange.setEndAfter(node)

        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(newRange)

        this.selectionRange = newRange
        return true
      } catch (e) {
        console.warn("Failed to insert at cursor:", e)
        return false
      }
    }

    return false
  }

  isSelectionInEditor() {
    if (!this.editorRef?.current) return false

    const selection = window.getSelection()
    if (selection.rangeCount === 0) return false

    const range = selection.getRangeAt(0)
    return this.editorRef.current.contains(range.commonAncestorContainer)
  }

  getSelectedText() {
    const selection = window.getSelection()
    return selection.toString()
  }

  getSelectedHTML() {
    const selection = window.getSelection()
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      const clonedSelection = range.cloneContents()
      const div = document.createElement('div')
      div.appendChild(clonedSelection)
      return div.innerHTML
    }
    return ''
  }

  selectAll() {
    if (!this.editorRef?.current) return false

    try {
      const selection = window.getSelection()
      const range = document.createRange()

      range.selectNodeContents(this.editorRef.current)
      selection.removeAllRanges()
      selection.addRange(range)

      this.selectionRange = range
      return true
    } catch (e) {
      console.warn("Failed to select all:", e)
      return false
    }
  }

  clearSelection() {
    const selection = window.getSelection()
    selection.removeAllRanges()
    this.selectionRange = null
  }

  moveToStart() {
    if (!this.editorRef?.current) return false

    try {
      const selection = window.getSelection()
      const range = document.createRange()

      range.setStart(this.editorRef.current, 0)
      range.setEnd(this.editorRef.current, 0)
      selection.removeAllRanges()
      selection.addRange(range)

      this.selectionRange = range
      return true
    } catch (e) {
      console.warn("Failed to move to start:", e)
      return false
    }
  }

  moveToEnd() {
    if (!this.editorRef?.current) return false

    try {
      const selection = window.getSelection()
      const range = document.createRange()

      range.selectNodeContents(this.editorRef.current)
      range.collapse(false)
      selection.removeAllRanges()
      selection.addRange(range)

      this.selectionRange = range
      return true
    } catch (e) {
      console.warn("Failed to move to end:", e)
      return false
    }
  }

  // Get current cursor position info
  getCursorInfo() {
    if (!this.selectionRange) return null

    const range = this.selectionRange
    const container = range.startContainer

    return {
      container,
      offset: range.startOffset,
      isTextNode: container.nodeType === Node.TEXT_NODE,
      parentElement: container.nodeType === Node.TEXT_NODE
        ? container.parentElement
        : container,
      isAtStart: range.startOffset === 0,
      isAtEnd: range.startOffset === (container.textContent?.length || 0)
    }
  }
}

// Export as a plugin-compatible object
export default {
  create: (options) => new SelectionManager(options),
  SelectionManager,
}